import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AumicoCoreProvider, NotificationsSocketService, NotificationType } from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider, OrganizationSettings } from '@modeso/aumico-lib-users-fe';
import { IStyleOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { IMycCompanyPreferences } from '@modeso/types__aumico-lib-companies-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { combineLatest, concat, EMPTY, from, of, timer } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take, timeout, withLatestFrom } from 'rxjs/operators';
import * as fromActions from '../actions/analytics.actions';
import {
    I18nLabelsForPdf,
    MultiYearPdfTronSwissMapper,
} from '../components/multi-year-comparison/pdf-tron-mapper/multi-year-pdf-tron-swiss.mapper';
import {
    AnalyticsCompanyEntry,
    selectAnalyticsFeature,
    selectAnalyticsForCompany,
} from '../reducer/analytics.reducer';
import { AumicoCompaniesService } from '../services/companies.service';
import { AnalyticsService } from '../services/analytics.service';

const debug = Debug('aumico:aumico-lib-companies-fe:AnalyticsEffects');

/** Single-shot WebSocket wait for the matching jobId; same timeout as V1 V2 endpoints. */
const PDF_RESULT_TIMEOUT_MS = 60_000;

/**
 * How often the `periodicFlush$` effect checks for dirty MYC-preferences
 * and writes them to the BE. 30s is a compromise between losing recent
 * changes on tab-close vs. spamming the rate-limited PATCH endpoint —
 * `ngOnDestroy` and the `companyId` switch trigger an immediate flush
 * for the predictable in-app navigation cases anyway, so this is the
 * fallback for "user keeps clicking and stays on the page".
 */
const MYC_PREFS_FLUSH_INTERVAL_MS = 30_000;

/**
 * Max wait for `OrganizationSettings` during a PDF export. Long enough for
 * the org-settings HTTP roundtrip on a slow connection; short enough that
 * a permanent failure (auth not done, BE 500) doesn't hang the export.
 * Falls back to `null` on timeout → resolveColors uses report-style.
 */
const ORG_SETTINGS_TIMEOUT_MS = 3_000;

@Injectable()
export class AnalyticsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly analyticsService: AnalyticsService,
        private readonly companiesService: AumicoCompaniesService,
        private readonly store: Store,
        private readonly i18n: I18NextService,
        private readonly mapper: MultiYearPdfTronSwissMapper,
        private readonly socketService: NotificationsSocketService,
        private readonly organizationProvider: OrganizationProvider,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        // Make sure organizationSettings is populated in the user-store —
        // PDF export reads it via `getOrganizationSettings()` to resolve the
        // color fallback when the master AR has `useCustomColors !== true`.
        // No-op when settings are already loaded.
        this.organizationProvider.dispatchGetOrganizationSettings();
    }

    /**
     * Bootstrap + initial load on `loadAnalyticsForCompany`. Single ordered
     * effect — the company fetch must complete BEFORE the analytics fetch so
     * that `hydrateMycPreferences` is in the store and the analytics fetch
     * can read `selectedLanguage` as `?language=`. Splitting this into two
     * parallel effects races the order, so we keep it as one chain here.
     *
     * Sequence per `loadAnalyticsForCompany`:
     *   1. GET company → extract `mycPreferences` (silently ignore fetch
     *      errors; we just load without prefs in that case).
     *   2. Emit `hydrateMycPreferences` (only if prefs were present) —
     *      reducer fires synchronously before the next action is processed.
     *   3. Snapshot the (now-hydrated) entry → read `selectedLanguage`.
     *   4. Issue the analytics fetch with the resolved `?language=`.
     *      - 200 → emit `loadAnalyticsSuccess`.
     *      - 409 `LANGUAGE_MISMATCH` → emit `requireLanguageSelection`
     *        (container opens modal).
     *      - other errors → `loadAnalyticsFailure`.
     */
    public loadAnalyticsForCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.loadAnalyticsForCompany),
            switchMap(({ companyId }) =>
                this.companiesService.getCompanyById(companyId).pipe(
                    map((company) => company?.mycPreferences),
                    catchError(() => of<IMycCompanyPreferences | undefined>(undefined)),
                    concatMap((preferences) => {
                        const fetch$ = this.fetchAnalyticsForCompany$(companyId);
                        if (preferences == null) {
                            // No persisted prefs → straight to fetch. The
                            // empty entry already has correct defaults.
                            return fetch$;
                        }
                        // `concat`: emit hydrate FIRST (reducer fires
                        // synchronously between emitted actions, so by the
                        // time `fetch$` subscribes it can read the
                        // already-hydrated `selectedLanguage`), THEN emit
                        // the fetch result.
                        return concat(
                            of(fromActions.hydrateMycPreferences({ companyId, preferences })),
                            fetch$,
                        );
                    }),
                ),
            ),
        ),
    );

    /**
     * Re-fetch triggered by the user explicitly picking a language (modal
     * confirm or controls-bar dropdown). The action carries the pinned
     * value; the reducer also wrote it to `entry.selectedLanguage` so a
     * subsequent reload will replay it.
     */
    public reloadOnLanguageChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setMultiYearLanguage),
            switchMap(({ companyId, language }) =>
                this.analyticsService.getMultiYearComparison$(companyId, language).pipe(
                    map((harmonized) =>
                        fromActions.loadAnalyticsSuccess({ companyId, harmonized }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        if (
                            error?.status === 409 &&
                            error?.error?.code === 'LANGUAGE_MISMATCH' &&
                            Array.isArray(error?.error?.availableLanguages)
                        ) {
                            return of(
                                fromActions.requireLanguageSelection({
                                    companyId,
                                    availableLanguages: error.error.availableLanguages,
                                }),
                            );
                        }
                        return of(fromActions.loadAnalyticsFailure({ companyId, error }));
                    }),
                ),
            ),
        ),
    );

    /**
     * Build the analytics-fetch observable for a given companyId, reading
     * the (already-hydrated) `selectedLanguage` from the store via
     * `take(1)`. Used inside the `loadAnalyticsForCompany$` chain.
     */
    private fetchAnalyticsForCompany$(companyId: string) {
        return this.store.select(selectAnalyticsForCompany(companyId)).pipe(
            take(1),
            switchMap((entry) => {
                const language = entry?.selectedLanguage ?? undefined;
                return this.analyticsService.getMultiYearComparison$(companyId, language).pipe(
                    map((harmonized) => {
                        debug(
                            'loadAnalyticsSuccess %s years=%d lang=%s sent=%s',
                            companyId,
                            harmonized.yearLabels.length,
                            harmonized.activeLanguage,
                            language ?? '∅',
                        );
                        return fromActions.loadAnalyticsSuccess({ companyId, harmonized });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (
                            error?.status === 409 &&
                            error?.error?.code === 'LANGUAGE_MISMATCH' &&
                            Array.isArray(error?.error?.availableLanguages)
                        ) {
                            return of(
                                fromActions.requireLanguageSelection({
                                    companyId,
                                    availableLanguages: error.error.availableLanguages,
                                }),
                            );
                        }
                        return of(fromActions.loadAnalyticsFailure({ companyId, error }));
                    }),
                );
            }),
        );
    }

    /**
     * Periodic flush — ticks every 30s and dispatches `flushMycPreferences`
     * for every company whose entry is dirty. Keeps the BE rate limiter
     * happy: 100 rapid clicks → 1 PATCH per 30s window.
     *
     * `timer(period, period)` starts after the first period (no immediate
     * tick at subscription time), so a freshly-loaded page doesn't
     * immediately re-PATCH whatever it just hydrated.
     *
     * The flush effect handles "nothing dirty" gracefully — `flushMycPreferences`
     * is a no-op when the entry isn't dirty, so dispatching speculatively
     * is fine.
     */
    public periodicFlush$ = createEffect(() =>
        timer(MYC_PREFS_FLUSH_INTERVAL_MS, MYC_PREFS_FLUSH_INTERVAL_MS).pipe(
            withLatestFrom(this.store.select(selectAnalyticsFeature)),
            mergeMap(([_, feature]) => {
                const dirtyCompanyIds = Object.keys(feature.byCompany).filter(
                    (id) => feature.byCompany[id]?.mycPreferencesDirty === true,
                );
                if (dirtyCompanyIds.length === 0) return EMPTY;
                return from(
                    dirtyCompanyIds.map((companyId) =>
                        fromActions.flushMycPreferences({ companyId }),
                    ),
                );
            }),
        ),
    );

    /**
     * Flush implementation. Listens to `flushMycPreferences` (from the
     * periodic timer OR explicit container-lifecycle dispatches) and:
     *   1. Snapshots the live entry.
     *   2. Returns EMPTY if not dirty (no-op).
     *   3. PATCHes the BE with the current persisted-field values.
     *   4. Dispatches `mycPreferencesFlushed` on success — reducer clears the
     *      dirty flag.
     *   5. On failure: stays dirty so the next periodic tick retries.
     *
     * `mergeMap` (not switchMap) so concurrent flushes for DIFFERENT companies
     * run in parallel. Within one company we don't usually have concurrent
     * flushes — the dirty flag is cleared right after the PATCH dispatches.
     */
    public flushMycPreferences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.flushMycPreferences),
            mergeMap(({ companyId }) =>
                this.store.select(selectAnalyticsForCompany(companyId)).pipe(
                    take(1),
                    switchMap((entry) => {
                        if (entry == null || !entry.mycPreferencesDirty) return EMPTY;
                        const partial = this.buildControlsBarPartial(entry);
                        return this.analyticsService.patchMycPreferences$(companyId, partial).pipe(
                            map(() => fromActions.mycPreferencesFlushed({ companyId })),
                            catchError(() => EMPTY),
                        );
                    }),
                ),
            ),
        ),
    );

    /**
     * Persistence — export-modal Confirm.
     *
     * Fires immediately (no debounce, no dirty-flag dance — single discrete
     * user action with an obvious intent to commit). Pushes the modal's
     * final values to `Company.mycPreferences`. Cancel does NOT dispatch
     * this action (per UX decision: only Confirm persists).
     */
    public persistExportModal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.persistExportModalConfirm),
            mergeMap(({ companyId, includeBalance, includeIncome, showAccounts, showRatios, selectedNodeIds }) =>
                this.analyticsService
                    .patchMycPreferences$(companyId, {
                        includeBalance,
                        includeIncome,
                        showAccounts,
                        showRatios,
                        selectedNodeIds,
                    })
                    .pipe(catchError(() => EMPTY)),
            ),
        ),
        { dispatch: false },
    );

    /**
     * Resolve the final color palette for the multi-year PDF, mirroring V1's
     * fallback rule (`print-options.component.ts:222`):
     *
     *   - Report has `styleOptions.useCustomColors === true`
     *       → use the report's own colors (user overrode at report level).
     *   - Otherwise (`false`, `undefined`, or no styleOptions at all)
     *       → fall back to the organization's `OrganizationSettings`.
     *       The org is the company-wide "master" theme; reports inherit
     *       unless they explicitly opt out.
     *   - When neither is available
     *       → return whatever styleOptions we had (could be undefined) and
     *         let the `.docx` template fall back to its static defaults.
     *
     * Always sets `useCustomColors: false` on the resolved object when we
     * filled it from org settings, so any downstream check on that flag
     * stays semantically correct.
     */
    private resolveColors(
        reportStyle: IStyleOptions | undefined,
        orgSettings: OrganizationSettings | null | undefined,
    ): IStyleOptions | undefined {
        if (reportStyle?.useCustomColors === true) {
            return reportStyle;
        }
        if (orgSettings != null) {
            return {
                titleColor: orgSettings.titleColor ?? '',
                textColor: orgSettings.textColor ?? '',
                backgroundColor: orgSettings.backgroundColor ?? '',
                tableHeaderColor: orgSettings.tableHeaderColor ?? '',
                tableFooterColor: orgSettings.tableFooterColor ?? '',
                backgroundColorPY: orgSettings.backgroundColorPY ?? '',
                useCustomColors: false,
            };
        }
        return reportStyle;
    }

    /** Build the PATCH body covering controls-bar persisted fields. */
    private buildControlsBarPartial(entry: AnalyticsCompanyEntry): IMycCompanyPreferences {
        const partial: IMycCompanyPreferences = {
            selectedYears: entry.controls.selectedYears,
            deltaMode: entry.controls.deltaMode,
            structMode: entry.controls.structMode,
        };
        // Only include `selectedLanguage` when the user has actually pinned
        // one. Sending `undefined` would be ignored by the BE allow-list
        // anyway, but staying explicit keeps audit logs clean.
        if (entry.selectedLanguage != null) {
            partial.selectedLanguage = entry.selectedLanguage;
        }
        return partial;
    }

    /**
     * Multi-year PDF export — mirrors the V2 PDFTron flow exactly:
     *   1. Build payload on the FE via `MultiYearPdfTronSwissMapper`
     *   2. POST to `/reporting/multiyear/pdf` (returns 202)
     *   3. Wait for `NotificationsSocketService.printJobCompleted$` (single-shot)
     *   4. Convert base64 → Blob → trigger browser download
     */
    public requestMultiYearPdf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.requestMultiYearPdf),
            mergeMap((action) => {
                const { companyId, selectedNodeIds, showAccounts, includeBalance, includeIncome, showRatios } = action;
                // Re-trigger the org-settings fetch right now. The HTTP is
                // a no-op if state is already populated (typical case); if it
                // wasn't yet, this kicks the load so the observable below
                // can emit a real value within the timeout window. The
                // earlier dispatch from the effect's constructor wasn't
                // enough — the class is constructed during DI bootstrap,
                // often before auth is fully wired up, and that request
                // silently goes nowhere.
                this.organizationProvider.dispatchGetOrganizationSettings();
                return combineLatest([
                    this.store.select(selectAnalyticsForCompany(companyId)).pipe(
                        filter((entry) => entry?.harmonized != null && entry?.controls != null),
                    ),
                    // Wait for the org settings to actually arrive (up to
                    // ORG_SETTINGS_TIMEOUT_MS). If they never come, accept
                    // `null` and let `resolveColors` fall back to report-
                    // style. This is the V1 fetch pattern (the print page
                    // also subscribes with `filter(s => s != null)`).
                    this.organizationProvider.getOrganizationSettings().pipe(
                        filter((s): s is OrganizationSettings => s != null),
                        take(1),
                        timeout({ first: ORG_SETTINGS_TIMEOUT_MS }),
                        catchError(() => of<OrganizationSettings | null>(null)),
                    ),
                ]).pipe(
                    take(1),
                    mergeMap(([entry, orgSettings]) => {
                        // Both streams emitted non-null values now (the
                        // upstream filters guarantee it), but be defensive
                        // for the type-narrowing.
                        if (!entry || !entry.harmonized || !entry.controls) {
                            return of(fromActions.requestMultiYearPdfFailure({
                                companyId,
                                error: 'No harmonized data loaded',
                            }));
                        }
                        // Resolve final color theme. V1's print-options.component.ts
                        // applies this same rule:
                        //   useCustomColors === true → report's saved colors
                        //   otherwise                → organizationSettings fallback
                        // Without this, exports of reports without
                        // `useCustomColors` would ship default / stale colors
                        // instead of the org's branding.
                        const resolvedColors = this.resolveColors(
                            entry.harmonized.styleOptions,
                            orgSettings,
                        );
                        // Pass the modal's selection + section toggles through to
                        // the mapper. When the action omits a flag (legacy callers),
                        // sections default to ON and selection defaults to "all".
                        const payload = this.mapper.map(
                            entry.harmonized,
                            entry.controls,
                            this.resolveLabels(),
                            {
                                selectedNodeIds: selectedNodeIds ? new Set(selectedNodeIds) : undefined,
                                showAccounts: !!showAccounts,
                                includeBalance: includeBalance !== false,
                                includeIncome: includeIncome !== false,
                                // Pass through only when set — `undefined` lets
                                // the mapper fall back to the in-app structMode.
                                showRatios: showRatios,
                                resolvedColors,
                            },
                        );
                        return this.analyticsService.requestMultiYearPdf$(payload as unknown as object).pipe(
                            mergeMap(() => {
                                // [MYC-DEBUG] 202 received; start of the 60s wait window for the websocket result.
                                console.log(`[MYC-DEBUG] FE MYC 202 received, waiting for print-job.completed @ ${new Date().toISOString()}`);
                                return this.socketService.printJobCompleted$.pipe(
                                    take(1),
                                    timeout(PDF_RESULT_TIMEOUT_MS),
                                    map((result) => {
                                        if (!result.success || !result.data) {
                                            throw new Error(result.error || 'PDF generation failed');
                                        }
                                        const companyName = entry.harmonized?.company?.name;
                                        this.triggerBlobDownload(result.data, this.buildFilename(companyName, companyId));
                                        return fromActions.requestMultiYearPdfSuccess({ companyId });
                                    }),
                                );
                            }),
                            catchError((error) => {
                                // [MYC-DEBUG] the 60s timeout / failure moment for MYC.
                                console.warn(`[MYC-DEBUG] FE MYC export FAILED/timeout: ${String(error)} @ ${new Date().toISOString()}`);
                                this.coreProvider.dispatchAddNotification(
                                    'aumico_pdf_my_export_failure_title',
                                    'aumico_pdf_my_export_failure_description',
                                    NotificationType.ERROR,
                                );
                                return of(fromActions.requestMultiYearPdfFailure({ companyId, error }));
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    private resolveLabels(): I18nLabelsForPdf {
        return {
            // TableHead carries just "in CHF" — section title is rendered above
            // the table by the .docx template. Mirrors V1's `wordBeforeCurrency`
            // exactly (pdf-tron-swiss-mapper.ts:1357).
            wordBeforeCurrency: this.i18n.t(
                'aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency',
            ) as string,
            // Bare-title FALLBACKS for `sectionToTitle` — used when the master
            // AR has no `reportSectionsTitles` override. Reuses V1's keys so
            // the multi-year PDF matches the existing annual-report PDFs.
            sectionTitleBalance: this.i18n.t('aumico_annual_report_print_preview_balance') as string,
            sectionTitleIncome: this.i18n.t('aumico_annual_report_print_preview_income') as string,
            // Δ column heading rendered into the TableHead row's `delta` cell.
            tableDeltaLabel: this.i18n.t('aumico_pdf_my_table_delta_label') as string,
        };
    }

    /** Convert base64 PDF → Blob → trigger download. Same conversion as V1 V2 effects. */
    private triggerBlobDownload(base64: string, filename: string): void {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * `Mehrjahresvergleich_<company>_<YYYY-MM-DD>.pdf`. Company name is sanitized
     * for filesystem safety (replaces `/ \\ : * ? " < > |` and collapses
     * whitespace). Falls back to the companyId when the name is missing or
     * the sanitization produces an empty string.
     */
    private buildFilename(companyName: string | undefined | null, companyId: string): string {
        const today = new Date().toISOString().split('T')[0];
        const sanitized = (companyName ?? '')
            .replace(/[\\/:*?"<>|]/g, '_')
            .replace(/\s+/g, ' ')
            .trim();
        const slug = sanitized.length > 0 ? sanitized : companyId;
        return `Mehrjahresvergleich_${slug}_${today}.pdf`;
    }
}
